import { deleteItem, readItem, updateItem } from '@directus/sdk';
import { useMutation, useQuery } from '@tanstack/react-query';

import type { Schema } from '../../../../types/DirectusSchema';
import { incompleteEventMapper } from '../../mappers/incompleteEvent';
import { directusClient } from '../../services/directusClient/directusClient.ts';
import { tanstackQueryClient } from '../../services/tanstackQueryClient.ts';

export const useIncompleteEventItem = (incompleteEventId: string) => {
  const query = useQuery({
    staleTime: 60 * (60 * 1000), // 60 mins
    queryKey: ['incomplete_events', { incompleteEventId }, 'to-form'],
    queryFn: async () => {
      if (!incompleteEventId) {
        return null;
      }

      const data = await directusClient.request(
        readItem('incomplete_events', incompleteEventId, {
          fields: [
            '*',
            {
              venue: ['id', 'commercial_name'],
              category: ['id', 'title'],
              target_audiences: ['*'],
              event_details: ['*'],
            },
          ],
        }),
      );

      return incompleteEventMapper.apiToForm(data as unknown as Schema['incomplete_events'][0]);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (updateData: Parameters<(typeof incompleteEventMapper)['formToApi']>[0]) => {
      if (!incompleteEventId || !query.data) {
        return { changes: false };
      }

      const apiData = incompleteEventMapper.formToApi(updateData);

      await directusClient.request(updateItem('incomplete_events', incompleteEventId, apiData));
    },
    onSuccess: async () => {
      await tanstackQueryClient.invalidateQueries({
        queryKey: ['incomplete_events', { incompleteEventId }],
        exact: false,
      });
    },
  });

  const removeMutation = useMutation({
    mutationFn: async () => {
      if (!incompleteEventId) {
        return;
      }

      await directusClient.request(deleteItem('incomplete_events', incompleteEventId));
    },
    onSuccess: async () => {
      tanstackQueryClient.removeQueries({
        queryKey: ['incomplete_events', { incompleteEventId }],
      });
      await tanstackQueryClient.invalidateQueries({
        queryKey: ['incomplete_events'],
        exact: false,
      });
    },
  });

  return { updateMutation, query, removeMutation };
};
